import { useLocation, useNavigate } from '@tanstack/react-router'
import {
    Activity,
    ChevronRight,
    CircleGauge,
    Eraser,
    GitCompare,
    History,
    Home,
    LogOut,
    MonitorDot,
    Power,
    RefreshCw,
    Settings,
    Sparkles,
    User,
    Users,
    LineChart,
    Zap,
    Layers,
    ChevronDown,
    Bell,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { useAIInsights } from '../../hooks/useAIInsights'
import { calculateAnalysis } from '../../lib/analysis-utils'
import { mapChainRowToOptionData } from '../../lib/data-utils'
import { TradeEngine } from '../../lib/trade-engine'
import { useMarketStore } from '../../services/store'
import { useSettingsStore } from '../../stores/settingsStore'
import type { UserSettings } from '../../types'
import { TradeSignalsModal } from '../ui/TradeSignalsModal'
import { AlertsManager } from '../alerts/AlertsManager'
import { NavItem } from './NavItem'
import { AITradeIdeasWidget } from '../analysis/AITradeIdeasWidget'
import { ThemeToggle } from './ThemeToggle'
import { MarketStatusIndicator } from './MarketStatusIndicator'
import { cn } from '../../lib/utils'

interface TopBarProps {
    onOpenSettings: () => void
    onToggleMobileMenu: () => void
}

export const TopBar: React.FC<TopBarProps> = ({ onOpenSettings, onToggleMobileMenu }) => {
    const { isAdmin, userData, signOut } = useAuth()
    const settingsExecutionMode = useSettingsStore(s => s.executionMode)
    const setExecutionMode = useSettingsStore(s => s.setExecutionMode)
    const navigate = useNavigate()
    const location = useLocation()

    const [showProfileMenu, setShowProfileMenu] = useState(false)
    const [showExtraPages, setShowExtraPages] = useState(false)
    const [isAlertsOpen, setIsAlertsOpen] = useState(false)
    const {
        isOpen: isAiModalOpen,
        response: aiRes,
        isGenerating,
        generateInsights,
        closeAiModal,
        AiModalComponent,
    } = useAIInsights()

    const [signals, setSignals] = useState<any[]>([])
    const [showSignals, setShowSignals] = useState(false)
    const [imgError, setImgError] = useState(false)
    const profileMenuRef = useRef<HTMLDivElement>(null)
    const extraPagesRef = useRef<HTMLDivElement>(null)

    // PERF: Build pseudoMarketData LAZILY inside handlers — NOT on every render.
    // Previously this ran .map() on every chain row on EVERY render (10+ times during init),
    // but it was only ever consumed by two click handlers.
    const buildPseudoMarketData = () => {
        const settings = useSettingsStore.getState()
        const store = useMarketStore.getState()
        const chain = store.data[settings.symbol] || []
        const spotPrice = store.indexLtp[settings.symbol] || 0
        return {
            options: chain.map((row) =>
                mapChainRowToOptionData(row, settings.symbol, settings.expiry || '', Date.now()),
            ),
            spotPrice,
            timestamp: Date.now(),
            vix: 0,
        } as any
    }

    const handleSignalGeneration = () => {
        const settings = useSettingsStore.getState()
        const pseudoMarketData = buildPseudoMarketData()
        if (!pseudoMarketData.options.length) return
        const analysis = calculateAnalysis(
            pseudoMarketData.options,
            settings.range,
            pseudoMarketData.spotPrice,
            settings.mode,
            settings.symbol,
        )
        const generated = TradeEngine.analyze(pseudoMarketData, analysis)
        setSignals(generated)
        setShowSignals(true)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setShowProfileMenu(false)
            }
            if (extraPagesRef.current && !extraPagesRef.current.contains(event.target as Node)) {
                setShowExtraPages(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // PERF FIX: Removed `useDashboardData()` — it was instance #1 (the FIRST to fire)
    // and caused TopBar to re-render on EVERY data change, but its output was ONLY
    // used inside the handleAiTradeIdeas click handler. Now we read data lazily.

    const handleAiTradeIdeas = async () => {
        const settings = useSettingsStore.getState()
        if (!settings.symbol || !settings.expiry) {
            alert('Please select a valid Symbol and Expiry first.')
            return
        }

        const pseudoMarketData = buildPseudoMarketData()
        const analysis = calculateAnalysis(
            pseudoMarketData.options,
            settings.range,
            pseudoMarketData.spotPrice,
            settings.mode,
            settings.symbol,
        )

        if (!pseudoMarketData?.options?.length) {
            alert('Market data not available.')
            return
        }

        try {
            const path = location.pathname.substring(1)
            const pageName = path || 'Global'

            let context: any = 'Dashboard'
            if (pageName.toLowerCase().includes('scanner')) context = 'Scanner'
            else if (pageName.toLowerCase().includes('crossover')) context = 'Crossover'
            else if (pageName.toLowerCase().includes('variance')) context = 'Variance'
            else if (pageName.toLowerCase().includes('inspector')) context = 'StrikeInspector'
            else if (pageName.toLowerCase().includes('home')) context = 'Home'

            // Consolidated Smart Signals Generation
            const generatedSignals = TradeEngine.analyze(pseudoMarketData, analysis)

            generateInsights(
                analysis,
                pseudoMarketData,
                settings as unknown as UserSettings,
                context,
                undefined,
                [], // History not needed for AI insights in TopBar
                generatedSignals,
            )
        } catch (error) {
            console.error('AI Error:', error)
        }
    }

    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768)
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    return (
        <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 h-[72px] gap-2 md:gap-3 lg:gap-4">
            <div className="flex items-center gap-3 shrink-0">
                <button
                    onClick={() => {
                        if (isMobile) {
                            onToggleMobileMenu()
                        } else {
                            navigate({ to: userData ? '/home' : '/' })
                        }
                    }}
                    className="flex items-center gap-3 group focus:outline-none"
                    title={isMobile ? 'Open Menu' : 'Go Home'}
                >
                    <div className="relative">
                        <div className="w-10 h-10 bg-[#005451] rounded-[12px] flex items-center justify-center text-white shadow-lg shadow-[#005451]/30 transition-all group-hover:scale-105 group-hover:shadow-[#005451]/40 group-active:scale-95 border border-white/20">
                            <Activity className="w-5 h-5 text-white drop-shadow-sm" />
                        </div>
                    </div>
                    <div className="flex flex-col items-start">
                        <h1 className="text-[22px] font-black text-slate-800 dark:text-white tracking-tight leading-none group-hover:text-[#005451] dark:group-hover:text-[#00a8a1] transition-colors">
                            Oracle
                        </h1>
                        <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:block">
                            Advanced Options
                        </span>
                    </div>
                </button>
            </div>

            <nav className="flex-1 hidden md:flex items-center justify-center">
                <div className="flex items-center gap-2 lg:gap-3">
                    <NavItem to="/home" icon={Home} label="Home" color="indigo" />
                    <NavItem to="/dashboard" icon={CircleGauge} label="Dashboard" color="indigo" />
                    <NavItem to="/analysis" icon={LineChart} label="Analysis" color="indigo" />
                    <NavItem to="/inspector" icon={MonitorDot} label="Inspector" color="indigo" />
                    <NavItem to="/backtest" icon={History} label="Backtest" color="emerald" />
                    {isAdmin && <NavItem to="/users" icon={Users} label="Users" color="rose" />}
                    
                    <div 
                        ref={extraPagesRef}
                        className="group relative flex items-center justify-center outline-none select-none shrink-0 cursor-pointer h-full z-50"
                        onClick={() => setShowExtraPages(prev => !prev)}
                        onMouseEnter={() => setShowExtraPages(true)}
                        onMouseLeave={() => setShowExtraPages(false)}
                    >
                        <div className={cn(
                            "relative flex items-center justify-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-3 md:py-1.5 lg:px-4 lg:py-2 rounded-full transition-colors duration-300",
                            showExtraPages 
                                ? "bg-slate-200/60 dark:bg-slate-800/60 text-slate-900 dark:text-slate-100" 
                                : "bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-100"
                        )}>
                            <Layers className="w-[16px] h-[16px] md:w-[18px] md:h-[18px]" strokeWidth={2.0} />
                            <span className={cn(
                                "text-[12px] md:text-[13px] tracking-tight capitalize whitespace-nowrap font-bold hidden lg:block",
                                showExtraPages ? "opacity-100" : "opacity-80 group-hover:opacity-100"
                            )}>
                                Extra Pages
                            </span>
                            <ChevronDown className={cn(
                                "w-3 h-3 transition-transform duration-200 hidden lg:block",
                                showExtraPages ? "opacity-100 rotate-180" : "opacity-50 group-hover:opacity-100"
                            )} />
                        </div>
                        <div className={cn(
                            "absolute top-full mt-2 left-1/2 -translate-x-1/2 min-w-[140px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-800 shadow-xl rounded-2xl transition-all duration-200 flex flex-col p-2 gap-1",
                            showExtraPages ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0"
                        )}>
                            <NavItem to="/crossover" icon={GitCompare} label="Crossover" color="indigo" onClick={() => setShowExtraPages(false)} />
                            <NavItem to="/variance" icon={Activity} label="Variance" color="indigo" onClick={() => setShowExtraPages(false)} />
                        </div>
                    </div>
                </div>
            </nav>

            <div className="flex items-center gap-2.5 shrink-0">
                <AITradeIdeasWidget />

                <button
                    onClick={() => setIsAlertsOpen(true)}
                    className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors relative outline-none ring-2 ring-transparent focus:ring-indigo-500/50"
                    title="Custom Alerts"
                >
                    <Bell size={18} strokeWidth={2.5} />
                </button>

                <MarketStatusIndicator />

                <ThemeToggle />

                <div className="relative" ref={profileMenuRef}>
                    <button
                        onClick={() => setShowProfileMenu(!showProfileMenu)}
                        className="flex items-center gap-2.5 p-1 rounded-full hover:bg-slate-100 transition-all active:scale-95 outline-none ring-2 ring-transparent hover:ring-indigo-100"
                    >
                        {userData?.photoURL && !imgError ? (
                            <div className="relative">
                                <img
                                    src={userData.photoURL}
                                    alt={userData.displayName || 'User'}
                                    className="w-9 h-9 rounded-full border-2 border-white shadow-sm object-cover"
                                    onError={() => setImgError(true)}
                                    crossOrigin="anonymous"
                                    referrerPolicy="no-referrer"
                                />
                                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></div>
                            </div>
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-linear-to-tr from-indigo-500 to-violet-500 flex items-center justify-center text-white font-black text-xs border-2 border-white shadow-sm uppercase">
                                {(userData?.displayName || userData?.email || 'U').charAt(0)}
                            </div>
                        )}
                        <div className="hidden xl:flex flex-col items-start pr-2">
                            <span className="text-xs font-black text-slate-800 leading-none">
                                {userData?.displayName?.split(' ')[0] || 'Account'}
                            </span>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                                {isAdmin ? 'Admin' : 'User'}
                            </span>
                        </div>
                    </button>

                    {showProfileMenu && (
                        <div className="absolute right-0 top-full mt-3 w-80 bg-white/70 dark:bg-slate-900/70 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-indigo-500/10 border border-white/50 dark:border-slate-800/50 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-300 origin-top-right ring-1 ring-white/60 dark:ring-slate-700/60">
                            <div className="relative p-8 flex flex-col items-center bg-linear-to-b from-slate-50/50 to-white/50 dark:from-slate-800/50 dark:to-slate-900/50 border-b border-gray-100 dark:border-slate-800">
                                <div className="absolute top-4 right-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-800 ring-1 ring-emerald-50 dark:ring-emerald-900/30">
                                    Online
                                </div>
                                <div className="relative mb-4 group cursor-pointer p-1 rounded-full bg-linear-to-tr from-indigo-500 via-purple-500 to-rose-500 shadow-lg shadow-indigo-500/20">
                                    <div className="bg-white dark:bg-slate-900 p-1 rounded-full">
                                        {userData?.photoURL && !imgError ? (
                                            <img
                                                src={userData.photoURL}
                                                alt={userData.displayName || 'User'}
                                                className="w-20 h-20 rounded-full shadow-inner object-cover"
                                                onError={() => setImgError(true)}
                                                crossOrigin="anonymous"
                                                referrerPolicy="no-referrer"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-800 dark:text-slate-200 font-black text-3xl">
                                                {(userData?.displayName || userData?.email || 'U')
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 text-center leading-tight mb-1">
                                    {userData?.displayName || 'Pro Trader'}
                                </h3>
                                <p className="text-xs text-slate-400 font-medium mb-4">{userData?.email}</p>
                                <div className="flex gap-2">
                                    <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-[10px] font-black uppercase tracking-wider rounded-lg border border-indigo-100 dark:border-indigo-800 shadow-sm">
                                        {isAdmin ? 'Admin' : 'User'}
                                    </span>
                                    <span className="px-3 py-1 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-wider rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                        Pro Member
                                    </span>
                                </div>
                            </div>

                            <div className="p-4 bg-white dark:bg-slate-900 space-y-2">
                                {/* Execution Mode Toggle (Moved Here) */}
                                <div className="px-1 py-1 mb-2">
                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3">
                                        System Mode
                                    </div>
                                    <div className="flex bg-slate-100/80 dark:bg-slate-800/80 p-1.5 rounded-2xl border border-slate-200/60 dark:border-slate-700/60 relative">
                                        <button
                                            onClick={() => setExecutionMode('PROD')}
                                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 ${
                                                settingsExecutionMode === 'PROD'
                                                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-md shadow-slate-200/50 dark:shadow-slate-900/50 scale-100 ring-1 ring-black/5 dark:ring-white/5'
                                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                                            }`}
                                        >
                                            <Zap
                                                size={14}
                                                className={
                                                    settingsExecutionMode === 'PROD'
                                                        ? 'text-amber-500 fill-amber-500'
                                                        : ''
                                                }
                                            />
                                            Live
                                        </button>
                                        <button
                                            onClick={() => setExecutionMode('BETA')}
                                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 ${
                                                settingsExecutionMode === 'BETA'
                                                    ? 'bg-linear-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-indigo-500/30 scale-100'
                                                    : 'text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-white/50 dark:hover:bg-slate-700/50'
                                            }`}
                                        >
                                            <Sparkles
                                                size={14}
                                                className={
                                                    settingsExecutionMode === 'BETA'
                                                        ? 'text-amber-200 animate-pulse'
                                                        : ''
                                                }
                                            />
                                            Beta
                                        </button>
                                    </div>
                                </div>
                                <div className="h-px bg-slate-100/50 dark:bg-slate-800/50 my-2 mx-4"></div>

                                <button
                                    onClick={() => {
                                        handleAiTradeIdeas();
                                        setShowProfileMenu(false);
                                    }}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all group relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-linear-to-r from-transparent via-violet-500/5 dark:via-violet-400/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                                    <div className="w-10 h-10 bg-slate-900 dark:bg-white rounded-xl text-violet-400 dark:text-violet-600 group-hover:bg-violet-600 group-hover:text-white dark:group-hover:bg-violet-500 dark:group-hover:text-white group-hover:shadow-lg group-hover:shadow-violet-500/20 transition-all flex items-center justify-center shrink-0">
                                        <Sparkles size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex flex-col items-start z-10">
                                        <span className="text-slate-900 dark:text-slate-100">AI Trade Ideas</span>
                                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tight group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                                            Smart Analytics
                                        </span>
                                    </div>
                                    <ChevronRight
                                        className="ml-auto text-slate-300 group-hover:text-violet-300 transition-colors z-10"
                                        size={20}
                                    />
                                </button>

                                <button
                                    onClick={() => {
                                        navigate({ to: '/users' })
                                        setShowProfileMenu(false)
                                    }}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all group"
                                >
                                    <div className="w-10 h-10 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-xl text-indigo-500 dark:text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-indigo-500/20 transition-all flex items-center justify-center">
                                        <User size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span className="text-slate-900 dark:text-slate-100">User Management</span>
                                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                            Access Control
                                        </span>
                                    </div>
                                    <ChevronRight
                                        className="ml-auto text-slate-300 group-hover:text-indigo-300 transition-colors"
                                        size={20}
                                    />
                                </button>

                                <button
                                    onClick={() => {
                                        onOpenSettings()
                                        setShowProfileMenu(false)
                                    }}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all group"
                                >
                                    <div className="w-10 h-10 bg-violet-50/50 dark:bg-violet-900/20 rounded-xl text-violet-500 dark:text-violet-400 group-hover:bg-violet-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-violet-500/20 transition-all flex items-center justify-center">
                                        <Settings size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span className="text-slate-900 dark:text-slate-100">Platform Settings</span>
                                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tight group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                                            System Config
                                        </span>
                                    </div>
                                    <ChevronRight
                                        className="ml-auto text-slate-300 group-hover:text-violet-300 transition-colors"
                                        size={20}
                                    />
                                </button>

                                <button
                                    onClick={() => {
                                        if (confirm('Are you sure you want to clear all data and reload?')) {
                                            localStorage.clear()
                                            window.location.reload()
                                        }
                                    }}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all group"
                                >
                                    <div className="w-10 h-10 bg-amber-50/50 dark:bg-amber-900/20 rounded-xl text-amber-500 dark:text-amber-400 group-hover:bg-amber-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-amber-500/20 transition-all flex items-center justify-center">
                                        <Eraser size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span className="text-slate-900 dark:text-slate-100">Reset Data</span>
                                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tight group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                                            Clear Cache & Reload
                                        </span>
                                    </div>
                                    <RefreshCw
                                        className="ml-auto text-slate-300 group-hover:text-amber-300 transition-colors"
                                        size={20}
                                    />
                                </button>

                                <div className="h-px bg-slate-100/50 dark:bg-slate-800/50 my-2 mx-4"></div>

                                <button
                                    onClick={() => {
                                        setShowProfileMenu(false)
                                        signOut()
                                    }}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-sm font-black text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-2xl transition-all group"
                                >
                                    <div className="w-10 h-10 bg-rose-50 dark:bg-rose-900/20 rounded-xl text-rose-500 dark:text-rose-400 group-hover:bg-rose-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-rose-500/20 transition-all flex items-center justify-center">
                                        <LogOut size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="flex flex-col items-start">
                                        <span>Log Out</span>
                                        <span className="text-[10px] text-rose-400 font-medium uppercase tracking-tight group-hover:text-white transition-all opacity-50">
                                            Safe Termination
                                        </span>
                                    </div>
                                    <Power
                                        className="ml-auto text-rose-200 dark:text-rose-800 group-hover:text-white transition-all animate-pulse"
                                        size={20}
                                    />
                                </button>
                            </div>

                            <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 text-center border-t border-slate-100 dark:border-slate-800">
                                <span className="text-xs font-medium text-slate-400">Options Oracle Terminal v2.0</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <AiModalComponent isOpen={isAiModalOpen} isLoading={isGenerating} response={aiRes} onClose={closeAiModal} />

            <TradeSignalsModal
                isOpen={showSignals}
                onClose={() => setShowSignals(false)}
                signals={signals}
                symbol={useSettingsStore.getState().symbol}
                onGenerateAiInsights={handleAiTradeIdeas}
            />
        </div>
    )
}
